import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

const ParticlesSwarm = ({ count = 15000, colorHex = 0x32966c }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    let width = window.innerWidth;
    let height = window.innerHeight;

    // SETUP
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x000000, 0.008);
    
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 2000);
    camera.position.set(0, 0, 120);

    const renderer = new THREE.WebGLRenderer({ 
      alpha: true, 
      antialias: true, 
      powerPreference: "high-performance" 
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    containerRef.current.appendChild(renderer.domElement);

    // POST PROCESSING
    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    
    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(width, height), 
      1.5, 0.4, 0.85
    );
    bloomPass.strength = 1.2; 
    bloomPass.radius = 0.5; 
    bloomPass.threshold = 0.1;
    composer.addPass(bloomPass);

    // OBJECTS
    const dummy = new THREE.Object3D();
    const target = new THREE.Vector3();
    const pColor = new THREE.Color();
    const clock = new THREE.Clock();
    
    const geometry = new THREE.TetrahedronGeometry(0.3);
    const material = new THREE.MeshBasicMaterial({ color: 0xffffff });
    
    const mesh = new THREE.InstancedMesh(geometry, material, count);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    scene.add(mesh);
    
    const positions = [];
    for(let i = 0; i < count; i++) {
      positions.push(new THREE.Vector3(
        (Math.random() - 0.5) * 100, 
        (Math.random() - 0.5) * 100, 
        (Math.random() - 0.5) * 100
      ));
      mesh.setColorAt(i, pColor.setHex(colorHex));
    }
    
    // PARAMS (Neural Matrix logic)
    const PARAMS = { radius: 80, activity: 6, complexity: 40, shellWidth: 4 };
    
    const animate = () => {
      const frameId = requestAnimationFrame(animate);
      const time = clock.getElapsedTime();
      
      for(let i = 0; i < count; i++) {
        const n = i / count;
        const phi = Math.acos(1.0 - 2.0 * n);
        const theta = Math.sqrt(count * Math.PI) * phi;
        
        const pulse = Math.sin(time * 1.5) * 2.0;
        const dataFlow = Math.sin(theta * PARAMS.complexity + time * PARAMS.activity);
        const neuralSpike = Math.pow(Math.abs(Math.sin(phi * 8.0 + time * 0.4)), 12.0) * 12.0;
        
        const r = PARAMS.radius + pulse + (dataFlow * PARAMS.shellWidth) + neuralSpike;
        
        const x = r * Math.sin(phi) * Math.cos(theta);
        const y = r * Math.sin(phi) * Math.sin(theta);
        const z = r * Math.cos(phi);
        
        target.set(x, y, z);
        
        const isSpike = neuralSpike > 4.0;
        const finalColorHex = isSpike ? 0xffd700 : colorHex; // Gold spikes, Emerald base
        
        pColor.setHex(finalColorHex);
        
        // Slower lerp for smoother movement
        positions[i].lerp(target, 0.05);
        dummy.position.copy(positions[i]);
        dummy.updateMatrix();
        
        mesh.setMatrixAt(i, dummy.matrix);
        mesh.setColorAt(i, pColor);
      }
      
      mesh.instanceMatrix.needsUpdate = true;
      mesh.instanceColor.needsUpdate = true;
      
      composer.render();
      return frameId;
    };

    let animationFrameId = animate();

    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      composer.setSize(width, height);
    };

    window.addEventListener('resize', handleResize);

    // CLEANUP
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
      
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      scene.clear();
    };
  }, [count, colorHex]);

  return <div ref={containerRef} id="particles-container" />;
};

export default ParticlesSwarm;
